// Root: the sandbox guard runs FIRST, in the root route's beforeLoad —
// before any child route (login included) loads or sends anything:
//
//   1. the configuration check (API base, host) — on failure nothing at
//      all is requested;
//   2. the unauthenticated probe GET /mock-admin/environment/public — its
//      banner and label must pass exactly as the signed-in probe must.
//
// A refusal throws, which stops every child beforeLoad and renders ONLY
// the blocking full-page error. Otherwise the persistent environment
// banner sits above every page, login included.
import { createRootRouteWithContext, Outlet, type ErrorComponentProps } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { BlockingError } from "@/components/BlockingError";
import { EnvBanner } from "@/components/EnvBanner";
import { API_BASE, errorMessage } from "@/lib/api";
import { fetchers, keys } from "@/lib/queries";
import { evaluateConfig, evaluateSandbox, type EnvironmentProbe, type GuardReason } from "@/lib/sandboxGuard";
import { useMe } from "@/lib/session";

interface RouterContext {
  queryClient: QueryClient;
}

class SandboxRefusal extends Error {
  reason: GuardReason;
  constructor(reason: GuardReason, message: string) {
    super(message);
    this.reason = reason;
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context }) => {
    const hostname = window.location.hostname;
    const config = evaluateConfig(hostname, API_BASE);
    if (!config.ok) throw new SandboxRefusal(config.reason, config.message);
    let env: EnvironmentProbe | null = null;
    try {
      env = await context.queryClient.ensureQueryData({
        queryKey: keys.publicEnvironment(),
        queryFn: fetchers.publicEnvironment,
        staleTime: 60_000,
      });
    } catch {
      env = null;
    }
    const verdict = evaluateSandbox({ hostname, apiBase: API_BASE, environment: env });
    if (!verdict.ok) throw new SandboxRefusal(verdict.reason, verdict.message);
  },
  component: RootLayout,
  errorComponent: RootError,
});

function RootError({ error }: ErrorComponentProps) {
  if (error instanceof SandboxRefusal) return <BlockingError reason={error.reason} message={error.message} />;
  return <BlockingError reason="UNEXPECTED" message={errorMessage(error)} />;
}

function RootLayout() {
  const me = useMe();
  return (
    <div className="app-root">
      <EnvBanner signedIn={!!me.data} />
      <div className="app-body">
        <Outlet />
      </div>
    </div>
  );
}
